import React, {
  useState,
  useContext,
  useCallback,
  useEffect,
  useRef,
  useMemo,
} from "react";
import { Stage, Layer, Rect, Transformer, Line, Image } from "react-konva";
import Konva from "konva";
import { Context } from "@routes/private/Editor/Context";
import {
  ICommonAttributes,
  IElement,
  IRawProps,
} from "@routes/private/Editor/types";
import ElementsFlattener from "./ElementsFlattener";
import ArtboardCanvasElement from "./ArtboardCanvasElement";
import NoArtboardCanvasElement from "./NoArtboardCanvasElement";
import TextEditAddIn from "./AddIns/TextEditAddIn";
import ElementLockedAddIn from "./AddIns/ElementLockedAddIn";
import { IGuidesRef, IProps, ISelectorProps } from "./types";
import { IDSConfig, ISelectorConfig } from "@state/dsconfig/types";
import * as S from "./styles";
import D from "@assets/themes/dimensions";
import C from "@assets/themes/colors";
import { IResizeValues } from "../CommonCommandsOptions/ResizeCommandOption/types";
import {
  stageZoom,
  centerStage,
  moveStage,
  moveDropAreas,
  moveElement,
  dropInArtboard,
  refreshStage,
  isLockedElement,
} from "@utils/editor";
import { IResizeValues as IArtboardResizeValues } from "@routes/private/Editor/Editor/CommandsBars/MainCommandBar/CommandsOptions/ResizeArtboardCommandOption/types";
import {
  moveSelector,
  initSelectionRect,
  cleanSelector,
  updateNodes,
  handleMouseMove,
  insideSelector,
  addToSelector,
  getElementsResize,
  getSelectedNodes,
  clickInsideArtboard,
  updateSelectorIcon,
} from "@utils/selector";
import RotationAddIn from "./AddIns/RotationAddIn";
import GuidesAddIn from "./AddIns/GuidesAddIn";
import DropAreasAddIn from "./AddIns/DropAreasAddIn";
import Menu from "../SelectDescribe/Menu";
import { debounce, over, set } from "lodash";
import selectors from "@state/selectors";
import { useSelector, useDispatch } from "react-redux";
import { showSnackBarAction } from "@state/openReducers/actions";
import { Html } from "react-konva-utils";
import { isEditingText } from "utils/text";
import { getElementsToUpdate, updateArtboard } from "@utils/artboard";
import { cloneDeep } from "lodash";
import { KonvaEventObject } from "konva/lib/Node";
import { PermissionEnum } from "@state/designs/types";
import { useLocation } from "react-router-dom";
import { AuthContext } from "@utils/DOIgnition/AuthContext";
import { IImageList, IState } from "../Pickers/ImagePicker/types";
import { useClipboardPaste, areFilesSame } from "@utils/clipboard";


interface PastedImage {
  id: string;
  src: string;
  x: number;
  y: number;
  imgObj: HTMLImageElement | null;
}

const Canvas: React.FC<IProps> = () => {
  const {
    data,
    editorStatus,
    stageRef,
    selectorActive,
    userPermission,
    selectDescribe,
    entityInformation,
    saveStatus,
    addInsStatus,
    artboardsZIndexChain,    
    addUpdatePreviews,
    onCommandChange,
    onSelectorActive,
    onZoomChange,
    onSelectedArtboardChange,
    onShowSubPicker,
    onRefreshStage,
    onSelectedElementsIdChange,
    onShowCropImage,
    onSavingPreview,
    onExportArtboardMedia,
    onOverElementIdChange,
    onSelectedElementIsEditing,
    onExportingCompress,
    onRotation,
    onSelectedText,    
  } = useContext(Context);

  const authContext = React.useContext(AuthContext);
  const location = useLocation();
  const searchParams = new URLSearchParams(location.search);
  const hashParams = new URLSearchParams(location.hash.replace("#", "?"));
  const update_previews =
    searchParams.get("update_previews") ??
    hashParams.get("update_previews") ??
    undefined;

  const guidesRef = useRef<IGuidesRef>(null);
  const showSelectDescribe: boolean = editorStatus.showSelectDescribe;

  const [isArtboard, setIsArtboard] = useState(true);
  const [dropAreas, setDropAreas] = useState([] as any[]);
  const dispatch = useDispatch();
  const dsConfig: IDSConfig = useSelector(selectors.dsConfig.getDSConfig);
  const selectorConfig: ISelectorConfig = dsConfig?.editorConfig
    ?.selectorConfig ?? { widt: 50, height: 50 };
  const layerRef = React.useRef<Konva.Layer>(null);
  const selector = React.useRef() as React.MutableRefObject<Konva.Rect>;
  const lockIconRef = React.useRef() as React.MutableRefObject<Konva.Image>;
  const selectorTrRef =
    React.useRef() as React.MutableRefObject<Konva.Transformer>;
  const [images, setImages] = useState<PastedImage[]>([]);
  const selectorPropsRef = React.useRef<ISelectorProps>({
    selecting: false,
    mouseDown: false,
    visible: false,
    x1: 0,
    y1: 0,
    x2: 0,
    y2: 0,
    nodesUpdateInfo: [] as any,
  });
  const backIds = React.useRef<string[]>([]);
  const selectedNode = React.useRef<any>({ node: undefined, trf: undefined });
  const [selectedNodes, setSelectedNodes] = useState<string[]>([]);
  const [arrowKeysActive, setArrowKeysActive] = useState(false);

  const [selectDescribeMenu, setSelectDescribeMenu] = useState<any | undefined>(
    undefined
  );
  const context = useContext(Context);
  const deltaBorder = 0;


  const [externalFile, setExternalFile] = useState<File|undefined>(undefined);

  // // USE CLIPBOARD FROM COPY/PASTE
  useClipboardPaste((e,file) => {
    console.log("copypaste canvas",file)
    if (file) {
      
      const areSame = editorStatus.clipboardExternalImage? areFilesSame(editorStatus.clipboardExternalImage, file): false;
      if (areSame){ 
        console.log("Not Copying from the Clipboard, Same File"); 
        context.onCommandChange("copypaste", {copy: false, paste: true});
        return;
      }
            
      setExternalFile(file);     
    }
    else
    {
      if (!isEditingText(e))
        context.onCommandChange("copypaste", {copy: false, paste: true});
    }
  });

  const debounceFn = useCallback(debounce(updateNodes, 500), []);

  const debounceFnMove = useCallback(
    debounce(() => {
      if (selectedNode.current) selectedNode.current.node.fire("dragend");
    }, 500),
    []
  );

  const debouncePreviews = useCallback(debounce(addUpdatePreviews, 500), []);
  const hasElementLocked = data
    ? isLockedElement(editorStatus?.selectedElementsId ?? [], data)
    : false;

  useEffect(() => {
    if (
      selectorTrRef.current &&
      lockIconRef.current &&
      editorStatus?.selectedElementsId.length > 1
    )
      updateSelectorIcon(
        selectorTrRef.current,
        lockIconRef.current,
        hasElementLocked
      );
  }, [hasElementLocked]);

  useEffect(() => {
    images.forEach((img, index) => {
      if (!img.imgObj) {
        const imgElement = new window.Image();
        imgElement.src = img.src;
        imgElement.onload = () => {
          setImages((prevImages) => {
            const newImages = [...prevImages];
            newImages[index] = { ...newImages[index], imgObj: imgElement };
            return newImages;
          });
        };
      }
    });
  }, [images]);

  const hideMouseUp = (e: any) => {
    selectorPropsRef.current.mouseDown = false;
    if (!selectorPropsRef.current.visible) return;
    if (
      selectorPropsRef.current.selecting &&
      selectorPropsRef.current.visible
    ) {
      let selectedelements: string[] = [];
      const elements: any = [];
      const selBox = selector.current.getClientRect();
      if (
        selBox.width >= selectorConfig.width ||
        selBox.height >= selectorConfig.height
      ) {
        layerRef.current?.getChildren().forEach((groupElementNode: any) => {
          const elBox =
            groupElementNode.attrs.type === "artboard"
              ? groupElementNode.children[0].getClientRect()
              : null;
          if (elBox && Konva.Util.haveIntersection(selBox, elBox)) {
            groupElementNode.children?.forEach((childrenNode: any) => {
              const childBox = childrenNode.getClientRect();
              if (
                Konva.Util.haveIntersection(selBox, childBox) &&
                childrenNode.attrs.id &&
                childrenNode.attrs.visible &&
                childrenNode.attrs.locked !== true
              ) {
                selectedelements.push(childrenNode.attrs.id);
                elements.push(childrenNode);
              } else if (
                childrenNode.attrs.type === "image" &&
                childrenNode.attrs.visible
              ) {
                const childImage = childrenNode.children[1];
                const childImageBox = childImage.getClientRect();
                if (
                  Konva.Util.haveIntersection(selBox, childImageBox) &&
                  childImage.attrs.id
                ) {
                  selectedelements.push(childImage.attrs.id);
                  elements.push(childImage);
                }
              }
            });
          }
        });
        if (selectedelements.length > 0)
          onSelectorActive("off", selectedelements);
      }
      selectorPropsRef.current.selecting = false;
      selectorPropsRef.current.mouseDown = false;
      selectorPropsRef.current.visible = false;
      selector.current.visible(false);
    }
  };

  const handleResize = (
    resizeValues: any,
    idElement: string,
    parentId?: string,
    notSave?: boolean
  ) => {
    if (
      selectorActive !== "active" ||
      editorStatus.selectedElementsId.length < 2
    ) {
      onCommandChange("resize", resizeValues, undefined, undefined, notSave);
    } else {
      let insideParent = true;
      let elements: any = [];
      let command = "";
      let artboard: any = undefined;

      elements = getSelectedNodes(layerRef.current, [idElement], elements);
      if (elements.length > 0) {
        artboard = dropInArtboard(layerRef, elements[0]);        
        if (artboard !== undefined && artboard.attrs.id !== parentId) {
          elements[0].remove();
          artboard.add(elements[0]);
        }

        command = artboard
          ? artboard.attrs.id === parentId
            ? "resize"
            : "moveElement"
          : "removeElement";
      }

      if (command === "moveElement") {
        resizeValues.elementInfo = resizeValues;
        resizeValues.oldParentId = parentId;
        resizeValues.newParentId = artboard.attrs.id;
      }
      resizeValues.id = idElement;
      resizeValues.command = command;

      selectorPropsRef.current.nodesUpdateInfo.push(resizeValues);

      if (
        selectorPropsRef.current.nodesUpdateInfo.length ===
          editorStatus.selectedElementsId.length &&
        !arrowKeysActive
      ) {
        let newValuesResize = getElementsResize(
          editorStatus.selectedElementsId,
          selectorPropsRef
        );        
        onCommandChange(
          "multiDragEnd",
          undefined,
          undefined,
          [...newValuesResize],
          notSave
        );
      }
    }
  };

  useEffect(() => {
    if (
      editorStatus.selectedElementsId.length > 0 &&
      !editorStatus.showSelectDescribe &&
      backIds.current.length === 0
    ) {
      backIds.current = cloneDeep(editorStatus.selectedElementsId);
      onSelectorActive("reload", []);
    } else if (
      selectorActive === "off" &&
      editorStatus.selectedElementsId.length < 2
    )
      cleanSelector(selectorTrRef, selectorPropsRef, layerRef);
    else if (editorStatus.selectedElementsId.length === 0)
      cleanSelector(selectorTrRef, selectorPropsRef, layerRef);
  }, [editorStatus.selectedElementsId]);

  useEffect(() => {
    if (backIds.current.length > 0) {
      if (selectorActive === "reload") {
        if (backIds.current.length === 1)
          onSelectorActive("off", backIds.current);
        else onSelectorActive("active", backIds.current);
      }
      if (selectorActive === "active") {
        backIds.current = [];
        addToSelector(
          layerRef,
          selectorTrRef,
          editorStatus.selectedElementsId,
          lockIconRef,
          hasElementLocked
        );
      }
    }
    if (selectorActive === "off") backIds.current = [];
  }, [selectorActive]);

  const keyBoard = (e: any) => {
    if (!isEditingText(e)) {
      if (e.key.includes("Arrow")) setArrowKeysActive(true);
      if (
        selectorTrRef.current?.getNodes().length > 1 &&
        e.key.includes("Arrow")
      ) {
        selectorPropsRef.current.nodesUpdateInfo = [];
        moveSelector(
          e,
          selectorTrRef.current,
          dsConfig.editorConfig.arrowsSpaceMovement
        );
        debounceFn(selectorTrRef.current);
      } else if (e.key.includes("Arrow") && selectedNode.current) {
        moveElement(
          e,
          selectedNode.current,
          dsConfig.editorConfig.arrowsSpaceMovement
        );
        debounceFnMove();
      }
    }
  };

  useEffect(() => {    
    if (!editorStatus.showSelectDescribe) {
      document.addEventListener("keydown", keyBoard);
      return () => document.removeEventListener("keydown", keyBoard);
    }
    if (data[0]?.children?.find((e) => e.type === "Artboard")) {
      setIsArtboard(true);
    }    

  }, []);

  useEffect(() => {
    if (stageRef.current?.getPointerPosition()) {
      stageZoom(stageRef, undefined, editorStatus.zoom);
      updateSelectorIcon(
        selectorTrRef.current,
        lockIconRef.current,
        hasElementLocked
      );
      moveDropAreas(layerRef.current, setDropAreas);
    }
  }, [editorStatus.zoom]);

  useEffect(() => {    
    if (stageRef.current && layerRef.current && data[0]?.children) {
      centerStage(
        stageRef.current,
        data[0].children,
        editorStatus.showSelectDescribe,
        onZoomChange
      );
      if (data[0].children.length > 0)
        moveDropAreas(layerRef.current, setDropAreas);
    }
  }, [layerRef.current]);

  useEffect(() => {
    if (stageRef.current && layerRef.current && data[0]?.children) {
      centerStage(
        stageRef.current,
        data[0].children,
        editorStatus.showSelectDescribe,
        onZoomChange
      );
      if (data[0].children.length > 0)
        moveDropAreas(layerRef.current, setDropAreas);
    }
  }, [editorStatus.showSelectDescribe]);

  useEffect(() => {
    if (
      stageRef.current &&
      layerRef.current &&
      data[0]?.children &&
      editorStatus.refreshStage
    ) {
      refreshStage(
        stageRef.current,
        data[0].children,
        editorStatus.showSelectDescribe,
        onZoomChange
      );
      if (data[0].children.length > 0)
        moveDropAreas(layerRef.current, setDropAreas);
      onRefreshStage(false);
    }
  }, [editorStatus.refreshStage]);

 

  useEffect(() => {
    if (externalFile) {             
      context.onPasteExternalImage(externalFile);
    }
  },[externalFile]);


  const isSelected = (elementId: string) => {
    return (
      editorStatus.selectedElementsId.includes(elementId) ||
      insideSelector(selectorPropsRef.current, elementId, selectedNodes)
    );
  };

  const handleMouseDownStage = (e: any) => {
    if (arrowKeysActive) return;
    if (e.evt.button === 2) return;
    setSelectDescribeMenu(undefined);
    if (e.target === e.target.getStage()) {
      handlMultiDragEnd();
      onSelectedArtboardChange();
    }
    if (!editorStatus.showSelectDescribe)
      initSelectionRect(e, selectorPropsRef, layerRef);

    if (editorStatus.currentSubPicker) onShowSubPicker(undefined);
  };

  const handleMouseDownSelector = (e: any) => {
    if (e.evt.button === 2) return;
    setSelectDescribeMenu(undefined);
    handlMultiDragEnd();
    onSelectedArtboardChange();
    if (editorStatus.currentSubPicker) onShowSubPicker(undefined);
  };

  const hanldeWheel = (event: any) => {
    event.evt.preventDefault();
    if (event.evt.altKey)
      onZoomChange(
        event.evt.deltaY < 0
          ? editorStatus.zoom.value + 5
          : editorStatus.zoom.value - 5,
        "pointer"
      );
    else {
      moveStage(stageRef, event);
      moveDropAreas(layerRef.current, setDropAreas);
    }
  };

  const handleDrag = (elementRef: any, trRef: any, containerId: string) => {
    
    if (!trRef.current) return;
    let artboard = dropInArtboard(layerRef, elementRef);
    let nodeTr = trRef.current;
    let layer = layerRef.current;

    /*if ( //Commented to avoid remove elements from artboard 8 abr 2025
      (artboard === undefined || artboard.attrs.id !== containerId) &&
      layer
    ) {
      layer.add(elementRef.current);
      layer.add(trRef.current);
    }*/

    if (artboard === undefined)
      nodeTr.setAttrs({
        borderStroke: C.LIGHT_GRAY,
        anchorStroke: C.LIGHT_GRAY,
      });
    else
      nodeTr.setAttrs({
        borderStroke: C.EDITOR_TRANSFORMER,
        anchorStroke: C.EDITOR_TRANSFORMER,
      });
  };

  const handleDragEnd = (
    elementRef: any,
    trRef: any,
    parentId: string,
    resizeValues: IResizeValues,
    ids: string[]
  ) => {    
    setArrowKeysActive(false);
    if (
      selectorActive === "active" &&
      editorStatus.selectedElementsId.length > 1
    ) {
      handleResize(resizeValues, ids[0], parentId);
      return;
    }
    const node = elementRef.current;
    let artboard = dropInArtboard(layerRef, elementRef, parentId);
    if (artboard === undefined) onCommandChange("removeElement", {});
    else if (artboard.attrs.id === parentId) {
      if (node && artboard) artboard.add(node);
      onCommandChange(
        "resize",
        resizeValues,
        ids,
        undefined,
        undefined,
        undefined,
        true
      );
    } else {
      let value = {
        elementInfo: resizeValues,
        oldParentId: parentId,
        newParentId: artboard.attrs.id,
      };
      onCommandChange("moveElement", value, ids);
    }
    if (guidesRef.current) guidesRef.current.onDragEnd();
  };

  const showTransfomerOutside = (
    tranformerRef: any,
    elementRef?: any,
    selected?: boolean
  ) => {
    if (layerRef.current && tranformerRef.current)
      layerRef.current.add(tranformerRef.current);
    if (selected) {
      selectedNode.current.node = elementRef.current;
      selectedNode.current.trf = tranformerRef.current;
    }
  };

  const handlMultiDragEnd = () => {
    if (editorStatus.showSelectDescribe) return;
    if (arrowKeysActive && !editorStatus.isShiftPressed) {
      let newValuesResize: any[] = getElementsResize(
        [...editorStatus.selectedElementsId],
        selectorPropsRef
      );
      if (newValuesResize.length > 0)
        onCommandChange(
          "multiDragEnd",
          undefined,
          undefined,
          newValuesResize,
          undefined,
          undefined,
          true
        );
      setArrowKeysActive(false);
    }
  };
  const handleSelect = (id: string) => {
    handlMultiDragEnd();
    onSelectedElementsIdChange(id);
  };

  const handleSelectArtboard = (
    id: string,
    event: KonvaEventObject<MouseEvent>
  ) => {
    handlMultiDragEnd();
    onSelectedArtboardChange(
      id,
      event?.evt?.shiftKey || editorStatus?.selectedElementsId?.length == 1
        ? undefined
        : "off"
    );
  };

  const handleArtboardResizeEnd = (
    newValues: IArtboardResizeValues,
    oldValues: IArtboardResizeValues,
    activeAnchor: string,
    nearbyArtboards: any
  ) => {
    let elementsUpdate = getElementsToUpdate(
      layerRef.current,
      newValues,
      oldValues,
      activeAnchor,
      nearbyArtboards,
      dsConfig?.editorConfig?.artboardResize
    );
    let ids: string[] = [];
    elementsUpdate.forEach((element: IArtboardResizeValues) =>
      ids.push(element.id)
    );
    onCommandChange("multiDragEnd", undefined, ids, elementsUpdate);
    setTimeout(() => {
      addUpdatePreviews();
    }, 3000);
  };

  const handleArtboardDragEnd = (
    newValues: IArtboardResizeValues,
    oldValues: IArtboardResizeValues
  ) => {    
    let elementsUpdate = updateArtboard(
      layerRef.current,
      stageRef,
      newValues,
      oldValues,
      dsConfig?.editorConfig?.artboardResize
    );
    if (elementsUpdate) {
      let ids: string[] = [];
      elementsUpdate.forEach((element: IArtboardResizeValues) =>
        ids.push(element.id)
      );
      onCommandChange("multiDragEnd", undefined, ids, elementsUpdate);
    } else
      dispatch(
        showSnackBarAction({
          messagge: "The artboard can not be moved over another artboard",
          typeMessage: "error",
          open: true,
        })
      );
  };

  const handleShowCrop = (
    show: boolean,
    cropMainGroupRef: any,
    cropTrRef: any
  ) => {
    if (
      show &&
      layerRef.current &&
      cropMainGroupRef.current &&
      cropTrRef.current
    ) {
      layerRef.current.add(cropMainGroupRef.current);
      layerRef.current.add(cropTrRef.current);
      layerRef.current.draw();
      //layerRef.current.batchDraw();
    }
    onShowCropImage(show);
  };

  const handleContextMenu = (e: any) => {
    e.evt.preventDefault();
    if (
      e.target === stageRef ||
      !editorStatus.showSelectDescribe ||
      editorStatus.selectedSelectDescribeCategoryId
    )
      return;
    if (stageRef.current && editorStatus.selectedElementsId.length > 0) {
      let mousePosition = stageRef.current.getPointerPosition();
      if (mousePosition)
        setSelectDescribeMenu({
          x: mousePosition.x,
          y: mousePosition.y,
        });
    }
  };

  const handleSavingPreview = (
    index: number,
    data: string[],
    name: string,
    inmediate?: boolean,
    updatePreviewsList?: boolean
  ) => {
    let maxGenerate =
      dsConfig.editorConfig.previewConfig.quantityGenerate ?? 99;
    if (index < maxGenerate) {
      onSavingPreview(data, name, inmediate);
      if (updatePreviewsList) debouncePreviews();
    }
  };

  const handleExportMedia = (
    data: string[],
    dealerId: string,
    collection: string,
    prefix: string,
    name: string,
    overwrite?: boolean,
    inmediate?: boolean,
    updatePreviewsList?: boolean
  ) => {
    const userData =
      authContext && authContext.userData ? authContext.userData : undefined;
    if (userData) {
      onExportArtboardMedia(
        data,
        dealerId,
        collection,
        (prefix ?? "") + name,
        userData.access_token,
        name,
        overwrite,
        inmediate
      );

      dispatch(
        showSnackBarAction({
          messagge: `Successfully ${
            overwrite ? "overwritten" : "added"
          } to Media Gallery, ${prefix ?? ""}${name}`,
          typeMessage: "success",
          open: true,
        })
      );
    }
  };

  const width =
    userPermission === PermissionEnum.EDIT
      ? editorStatus.showSelectDescribe
        ? window.innerWidth - D.SELECT_DESCRIBE
        : window.innerWidth - D.TOOL_BAR_WIDTH
      : window.innerWidth;
  const height =
    window.innerHeight -
    (editorStatus.showSelectDescribe
      ? D.APP_BAR_HEIGHT
      : D.APP_BAR_HEIGHT + D.COMMAND_BAR_HEIGHT);

  const gridSpace = dsConfig.editorConfig.gridOverlayConfig.gridSpace;
  const gridWidth = width + gridSpace + gridSpace;
  const gridStroke = dsConfig.editorConfig.gridOverlayConfig.gridStroke;
  const strokeColor = dsConfig.editorConfig.gridOverlayConfig.gridStrokeColor;

  const createLineGrids = (grid: number) => {
    let linesA = [];
    let linesB = [];
    for (let i = 0; i < gridWidth / grid; i++) {
      linesA.push(
        <Line
          strokeWidth={gridStroke}
          stroke={strokeColor}
          points={[
            i * grid - gridSpace,
            0 - gridSpace,
            i * grid - gridSpace,
            gridWidth - gridSpace,
          ]}
        />
      );

      linesB.push(
        <Line
          strokeWidth={gridStroke}
          stroke={strokeColor}
          points={[
            0 - gridSpace,
            i * grid - gridSpace,
            gridWidth - gridSpace,
            i * grid - gridSpace,
          ]}
        />
      );
    }
    return [linesA, linesB];
  };
  const [linesA, linesB] = useMemo(
    () =>
      createLineGrids(
        editorStatus.gridOverlay?.size ??
          dsConfig.editorConfig.gridOverlayConfig.defaultSize
      ),
    [editorStatus.gridOverlay?.size]
  );

  const renderLevel = (element: IElement, artboardIndex: number) => {
    return (
      <ElementsFlattener
        key={element.id}
        dataElement={element}
        artboardIndex={artboardIndex}
        editorStatus={editorStatus}
        selectDescribe={selectDescribe}
        entityInformation={entityInformation}
        selectorActive={selectorActive}
        artboardsZIndexChain={artboardsZIndexChain}
        dsConfig={dsConfig}
        selectorProps={selectorPropsRef.current}
        selectedNodes={selectedNodes}
        onCommandChange={onCommandChange}
        onOverElementIdChange={onOverElementIdChange}
        onSelectedElementIsEditing={onSelectedElementIsEditing}
        onRotation={onRotation}
        isSelected={isSelected}
        handleDrag={handleDrag}
        handleSelect={handleSelect}
        showTransfomerOutside={showTransfomerOutside}
        handleResize={handleResize}
        handleDragEnd={handleDragEnd}
        handleShowCrop={handleShowCrop}
      />
    );
  };

  return (
    <S.Root dragging={editorStatus.dragging}>
      {editorStatus.showSelectDescribe && selectDescribeMenu && (
        <Menu
          position={selectDescribeMenu}
          closeMenu={() => setSelectDescribeMenu(undefined)}
          limits={{ width: width, height: height }}
          fields={dsConfig?.selectDescribeConfig.categories}
        />
      )}
      <DropAreasAddIn dropAreas={dropAreas} />
      <Stage
        ref={stageRef}
        width={width}
        height={height}
        onMouseDown={(e) => handleMouseDownStage(e)}
        onMouseMove={(e) =>
          editorStatus.isShiftPressed &&
          handleMouseMove(
            e,
            selectorPropsRef,
            selector,
            layerRef,
            setSelectedNodes,
            selectorConfig
          )
        }
        onMouseUp={(e) => hideMouseUp(e)}
        onWheel={(e) => !selectDescribeMenu && hanldeWheel(e)}
        draggable={editorStatus.dragging}
        onDragEnd={(e) => moveDropAreas(layerRef.current, setDropAreas)}
        onContextMenu={(e) => handleContextMenu(e)}
      >
        <Layer
          ref={layerRef}
          onDragMove={(e) => {
            if (guidesRef.current) guidesRef.current.onDragMove(e);
          }}
          onDragEnd={(e) => {
            if (guidesRef.current) guidesRef.current.onDragEnd();
          }}
          imageSmoothingEnabled={false}
        >
          {isArtboard ? (
            data[0].children?.map((element, index) => (
              <ArtboardCanvasElement
                key={element.id}
                parentElement={data[0]}
                element={element}
                isSelected={
                  editorStatus.selectedArtboardIds?.find(
                    (id: string) => id === element.id
                  ) === undefined
                    ? false
                    : true
                }
                onSelect={(event: KonvaEventObject<MouseEvent>) =>
                  editorStatus.editMode &&
                  handleSelectArtboard(element.id, event)
                }
                showSelectDescribe={showSelectDescribe}
                exportingValues={
                  dsConfig &&
                  dsConfig.editorConfig &&
                  dsConfig.editorConfig.exportingValues
                }
                exportingSizes={editorStatus.exportingSizes}
                exporting={editorStatus.exporting}
                exportingProperties={editorStatus.exportingProperties}
                exportingArtboards={editorStatus.selectedArtboardIds}
                onExportingCompress={(size?: number, ratio?: number) =>
                  onExportingCompress(size, ratio, element.id)
                }
                savingParsedTree={saveStatus.savingParsedTree}
                savingPreview={(
                  data: string[],
                  name: string,
                  inmediate?: boolean,
                  updatePreviewsList?: boolean
                ) =>
                  handleSavingPreview(
                    index,
                    data,
                    name,
                    inmediate,
                    updatePreviewsList
                  )
                }
                exportMedia={(
                  data: string[],
                  dealerId: string,
                  collection: string,
                  prefix: string,
                  name: string,
                  overwrite?: boolean,
                  inmediate?: boolean,
                  updatePreviewsList?: boolean
                ) =>
                  handleExportMedia(
                    data,
                    dealerId,
                    collection,
                    prefix,
                    name,
                    overwrite,
                    inmediate,
                    updatePreviewsList
                  )
                }
                zoom={editorStatus.zoom}
                previewConfig={
                  dsConfig &&
                  dsConfig.editorConfig &&
                  dsConfig.editorConfig.previewConfig
                }
                editMode={editorStatus.editMode}
                artboardResize={
                  dsConfig?.editorConfig?.artboardResize ?? {
                    min: 10,
                    max: 4000,
                    minSpaceX: 50,
                    minSpaceY: 50,
                  }
                }
                onResize={(
                  newValues: IArtboardResizeValues,
                  oldValues: IArtboardResizeValues,
                  activeAnchor: string,
                  nearbyArtboards: any
                ) =>
                  handleArtboardResizeEnd(
                    newValues,
                    oldValues,
                    activeAnchor,
                    nearbyArtboards
                  )
                }
                onDragEnd={(
                  newValues: IArtboardResizeValues,
                  oldValues: IArtboardResizeValues
                ) => handleArtboardDragEnd(newValues, oldValues)}
                layer={layerRef.current}
                updatePreviews={update_previews}
                previews={entityInformation?.previews}
              >
                {renderLevel(element, index)}
              </ArtboardCanvasElement>
            ))
          ) : (
            <NoArtboardCanvasElement
              exportingValues={
                dsConfig &&
                dsConfig.editorConfig &&
                dsConfig.editorConfig.exportingValues
              }
              exportingSizes={editorStatus.exportingSizes}
              exporting={editorStatus.exporting}
              exportingProperties={editorStatus.exportingProperties}
              exportingArtboards={editorStatus.selectedArtboardIds}
              onExportingCompress={(size?: number, ratio?: number) =>
                onExportingCompress(size, ratio, undefined)
              }
              savingParsedTree={saveStatus.savingParsedTree}
              savingPreview={(data: string[], name: string) =>
                onSavingPreview(data, name)
              }
              zoom={editorStatus.zoom}
              previewConfig={
                dsConfig &&
                dsConfig.editorConfig &&
                dsConfig.editorConfig.previewConfig
              }
              showSelectDescribe={showSelectDescribe}
            >
              {data[0] && renderLevel(data[0], 0)}
            </NoArtboardCanvasElement>
          )}
          {images.map((img) =>
            img.imgObj ? (
              <Image
                key={img.id}
                image={img.imgObj}
                x={img.x}
                y={img.y}
                draggable
              />
            ) : null
          )}
          <Transformer
            ref={selectorTrRef}
            shouldOverdrawWholeArea={
              !editorStatus.isShiftPressed && !hasElementLocked
            }
            rotateEnabled={false}
            boundBoxFunc={(oldBox: any, newBox: any) => {
              if (newBox.width < 5 || newBox.height < 5) {
                return oldBox;
              }
              return newBox;
            }}
            enabledAnchors={
              hasElementLocked
                ? []
                : ["top-left", "top-right", "bottom-left", "bottom-right"]
            }
            onMouseUp={(e) => {
              if (
                clickInsideArtboard(
                  stageRef.current,
                  stageRef.current?.getPointerPosition()
                ) === false
              )
                handleMouseDownSelector(e);
            }}
            onDragStart={(e) => {
              selectorPropsRef.current.nodesUpdateInfo = [];
              setArrowKeysActive(false);
            }}
          >
            <ElementLockedAddIn ref={lockIconRef} />
          </Transformer>
          <Rect
            ref={selector}
            visible={false}
            strokeWidth={0.5}
            stroke="#09a4ff"
          />
          <Html>
            <TextEditAddIn
              editorStatus={editorStatus}
              data={data}
              onCommandChange={onCommandChange}
              onSelectedElementIsEditing={onSelectedElementIsEditing}
              onSelectedText={onSelectedText}
            />
            <RotationAddIn
              visible={addInsStatus.addInRotationStatus.visible}
              rotation={addInsStatus.addInRotationStatus.rotation}
              x={addInsStatus.addInRotationStatus.x}
              y={addInsStatus.addInRotationStatus.y}
            />
          </Html>
          <GuidesAddIn
            ref={guidesRef}
            stage={stageRef.current}
            artboards={data}
            selectedElementsId={editorStatus.selectedElementsId}
          />
        </Layer>
        {editorStatus.gridOverlay && editorStatus.gridOverlay.show && (
          <Layer>
            {linesA}
            {linesB}
          </Layer>
        )}
      </Stage>
    </S.Root>
  );
};

export default Canvas;
